import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { toast } from '@atrvd/saas-ui'
import { isSessionExpiredToast } from '@/lib/session-expired-toast'

/**
 * History `state` for a navigation that changes the URL but not the screen —
 * e.g. renaming a manual bank moves its detail page, whose address is the name.
 * The sweep skips such a navigation, so an error the screen just raised (a
 * partial save failure) is not swept by the screen's own redirect. Only that
 * navigation is exempt: the next one the user makes sweeps as usual.
 */
export const SAME_SCREEN_NAVIGATION = { sameScreen: true } as const

function isSameScreenNavigation(state: unknown): boolean {
  return typeof state === 'object' && state !== null && 'sameScreen' in state && state.sameScreen === true
}

/**
 * Clear error toasts when the user moves to another page (DCP-365 AC.3).
 *
 * Error toasts no longer close themselves (AC.1), which makes them outlive the
 * screen that raised them: routing here is client-side, so the Toaster never
 * unmounts and a toast about a failed import would follow the user onto an
 * unrelated page with no way to tell what it referred to. A message belongs to
 * the screen that produced it.
 *
 * The other half of AC.3 — "or refreshes the page" — needs no code: toasts live
 * in memory and a document reload takes them with it. What needs code is the
 * EXCEPTION to that, which is why {@link restoreSessionExpiredToast} exists.
 *
 * Only errors are swept. Success, info and warning toasts still close on their
 * own timer and are typically gone before any navigation; dismissing them here
 * would cut short a confirmation the user may not have read yet.
 */
export function useDismissErrorToastsOnNavigate(): void {
  const { pathname, state } = useLocation()
  const navigationType = useNavigationType()
  /**
   * The pathname on FIRST render is not a navigation — it is the page the user
   * opened. Sweeping there would kill a toast raised during startup.
   *
   * This skips the first pathname only, NOT the redirect that may follow it: the
   * index routes in App render `<Navigate to="dashboard" replace />`, so opening
   * `/banking` commits `/banking`, then immediately commits
   * `/banking/dashboard`, and the second commit does count as a change here
   * (PR #210 review #5).
   *
   * Left that way deliberately. Telling a redirect from a real navigation needs
   * a heuristic — navigation type, timing — and every such heuristic is wrong in
   * one of two directions. Being wrong towards sweeping destroys a message the
   * user needed; being wrong the other way leaves a stale toast, which the next
   * navigation clears anyway. Neither is worth buying with a rule nobody can
   * predict. In practice nothing is lost: the redirect commits during mount,
   * before any request could have failed, and the one toast that CAN be present
   * that early is the restored session-expired one, which is exempt by id. A
   * test pins this behaviour so it is a decision rather than an accident.
   */
  const previousPathname = useRef<string | null>(null)

  useEffect(() => {
    const isFirstRender = previousPathname.current === null
    const changed = previousPathname.current !== pathname
    previousPathname.current = pathname
    if (isFirstRender || !changed) return
    // A POP (Back/Forward) restores the entry's state too, so returning to a
    // renamed page later would wrongly skip the sweep; honour the marker only
    // on the navigation that set it.
    if (navigationType !== 'POP' && isSameScreenNavigation(state)) return

    // Read the live toast list rather than tracking ids ourselves: sonner
    // already knows which toasts are on screen and what kind each is, and a
    // parallel registry would drift from it the moment a toast closed by any
    // route we did not intercept.
    for (const active of toast.getToasts()) {
      // getToasts() also yields dismissal records (`{ id, dismiss: true }`),
      // which carry no `type` — the `type === 'error'` test skips them, so
      // there is no need to narrow the union first.
      if ('type' in active && active.type === 'error' && !isSessionExpiredToast(active.id)) {
        toast.dismiss(active.id)
      }
    }
  }, [pathname, state, navigationType])
}
